import React from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Divider,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { invoiceStatusStyles } from "./orderManagementUtils";

const totalsLabel = (totals) => `${totals.hours} Std · Mat.: ${totals.materials}`;

export default function OrderCardList({
    orders,
    loadingOrders,
    loadingArticles,
    deletingOrder,
    onEditOrder,
    onDeleteOrder,
    onCreateInvoice,
    onCreateFirstOrder,
    orderTotals,
    resolveInvoiceStatus,
    resolveCustomerLabel,
}) {
    return (
        <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
            {(loadingOrders || loadingArticles) && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                </Box>
            )}

            <Grid2 container spacing={2} sx={{ mt: 1 }}>
                {!loadingOrders && orders.length === 0 && (
                    <Grid2 xs={12}>
                        <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                            <CardContent>
                                <Typography variant="body1">Noch keine Aufträge vorhanden.</Typography>
                                <Button
                                    variant="contained"
                                    sx={{ mt: 2, bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                                    onClick={onCreateFirstOrder}
                                >
                                    Ersten Auftrag anlegen
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid2>
                )}

                {orders.map((order, index) => {
                    const totals = orderTotals(order);
                    const invoiceStatus = resolveInvoiceStatus(order);
                    const statusStyles = invoiceStatusStyles[invoiceStatus] || invoiceStatusStyles.none;
                    const customerLabel = order.customer || resolveCustomerLabel(order.customerId) || "—";

                    return (
                        <Grid2 key={index} xs={12} sm={6} md={4} lg={3}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "0 6px 24px rgba(0,0,0,0.45)"
                                            : "0 6px 24px rgba(0,0,0,0.06)",
                                    border: (theme) =>
                                        theme.palette.mode === "dark" ? `1px solid ${theme.palette.divider}` : statusStyles.border,
                                    background: (theme) =>
                                        theme.palette.mode === "dark" ? theme.palette.background.paper : statusStyles.background,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="h6" fontWeight={700} noWrap title={order.title || "Unbenannter Auftrag"}>
                                                {order.title || "Unbenannter Auftrag"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Kunde: {customerLabel}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Chip size="small" label={`${order.performanceDays.length} Ltg.`} />
                                            <Chip size="small" label={`${order.materials.length} Mat.`} />
                                        </Stack>
                                    </Stack>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                        Leistungen
                                    </Typography>
                                    <List dense sx={{ py: 0 }}>
                                        {order.performanceDays.length === 0 && (
                                            <ListItem>
                                                <ListItemText
                                                    primary="Keine Leistungen erfasst."
                                                    primaryTypographyProps={{ color: "text.secondary" }}
                                                />
                                            </ListItem>
                                        )}
                                        {order.performanceDays.map((day, i) => (
                                            <ListItem key={i}>
                                                <ListItemText
                                                    primary={`${day.date || "?"} – ${day.articleName || "?"} (${day.amount ?? "?"})`}
                                                    secondary={`von ${day.start || "?"} bis ${day.end || "?"}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>

                                    {order.materials.length > 0 && (
                                        <>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                                Materialien
                                            </Typography>
                                            <List dense sx={{ py: 0 }}>
                                                {order.materials.map((material, i) => (
                                                    <ListItem key={i}>
                                                        <ListItemText primary={`${material.articleName} (${material.amount})`} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}
                                </CardContent>

                                <CardActions sx={{ mt: "auto" }}>
                                    <Tooltip title={totalsLabel(totals)}>
                                        <Chip size="small" variant="outlined" label={totalsLabel(totals)} />
                                    </Tooltip>
                                    <Box sx={{ flex: 1 }} />
                                    <Button size="small" onClick={() => onEditOrder(index)}>
                                        Bearbeiten
                                    </Button>
                                    <Button size="small" color="error" disabled={deletingOrder} onClick={() => onDeleteOrder(index)}>
                                        Löschen
                                    </Button>
                                    <Button size="small" onClick={() => onCreateInvoice(order)}>
                                        Rechnung
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid2>
                    );
                })}
            </Grid2>
        </Container>
    );
}
